import React, { useState, useContext, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Checkbox, FormControlLabel, Typography, Box } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import { useAuth0 } from "@auth0/auth0-react";
import PremPlayersContext from "../../contexts/premplayers-context";
import UserPendingTransactionsContext from "../../contexts/user-pending-transactions-context";
import { LeaderboardContext } from "../../contexts/leaderboard-context";
import { SiteVarsContext } from "../../contexts/sitevars-context";
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";
import { confirmTeamResource, getConfirmedTeamResource } from "../../services/transfers.service";
import { getClubOffersResource } from "../../services/offers.service";

export const ManageTeam = () => {
  const { getPlayerPositionById, getTeamById, getCurrentWindow } = useSiteVarsFunctions();
  const { premPlayersData, isLoadingPremPlayersData } = useContext(PremPlayersContext);
  const { userPendingTransactionsData, isLoadingUserPendingTransactionsData } = useContext(UserPendingTransactionsContext);
  const { leaderboardData } = useContext(LeaderboardContext);
  const { siteVarsData } = useContext(SiteVarsContext);
  const { user } = useAuth0();

  const [userPlayers, setUserPlayers] = useState([]);
  const [wonPlayers, setWonPlayers] = useState([]);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(true);
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [isTeamConfirmed, setIsTeamConfirmed] = useState(false);
  const [confirmedPlayerIds, setConfirmedPlayerIds] = useState([]);
  const [acceptedOffers, setAcceptedOffers] = useState([]);
  const [isLoadingAcceptedOffers, setIsLoadingAcceptedOffers] = useState(true);

  useEffect(() => {
    if (user && leaderboardData.length > 0 && premPlayersData.length > 0) {
        const clubId = user['https://tlog-online.co.uk/club_id'];
        let userTeam = leaderboardData.find(team => team.clubId == clubId);
        userTeam.players = userTeam.players.filter(player => player.transfer != "in");
        
        if (userTeam && userTeam.players) {
            const players = userTeam.players.map(player => {
                const fullPlayerData = premPlayersData.find(p => p.playerId === player.playerId);
                return { ...player, ...fullPlayerData };
            });
            setUserPlayers(players);
        }
        setIsLoadingTeamData(false);
    }
}, [user, leaderboardData, premPlayersData]);

  useEffect(() => {
    const fetchConfirmedTeam = async () => {
      if (user && siteVarsData && siteVarsData.game_vars) {
        const clubId = user['https://tlog-online.co.uk/club_id'];
        const windowId = siteVarsData.game_vars.currentWindow;
        const { data, error } = await getConfirmedTeamResource(clubId, windowId);

        if (data && data.team && data.team.length > 0) {
          setIsTeamConfirmed(true);
          setConfirmedPlayerIds(data.team);
          const initialSelected = {};
          data.team.forEach(playerId => {
            initialSelected[playerId] = true;
          });
          setSelectedPlayers(initialSelected);
        } else if (error) {
          console.error("Error fetching confirmed team:", error);
        }
      }
    };

    fetchConfirmedTeam();
  }, [user, siteVarsData]);

  useEffect(() => {
    const fetchAcceptedOffers = async () => {
      if (user && siteVarsData && siteVarsData.game_vars && premPlayersData.length > 0) {
        const clubId = user['https://tlog-online.co.uk/club_id'];
        const windowId = siteVarsData.game_vars.currentWindow;
        
        const { data, error } = await getClubOffersResource(clubId, windowId);

        if (data && data.offers) {
          data.offers = data.offers.filter(offer => offer.status == "accepted");
          const offersWithPlayerData = data.offers.map(offer => {
            const fullPlayerData = premPlayersData.find(p => p.playerId === offer.playerId);
            return { ...offer, ...fullPlayerData };
          });
          setAcceptedOffers(offersWithPlayerData);
        } else if (error) {
          console.error("Error fetching accepted offers:", error);
        }
        setIsLoadingAcceptedOffers(false);
      }
    };

    fetchAcceptedOffers();
  }, [user, siteVarsData, premPlayersData]);

  useEffect(() => {
    if (finalPlayers.length > 0) {
      const initialSelectedPlayers = {};
      finalPlayers.forEach(player => {
        if (player.bidId || player.isOfferPlayer) { // Won players and offer players are initially selected and disabled
          initialSelectedPlayers[player.playerId] = true;
        }
      });
      setSelectedPlayers(initialSelectedPlayers);
    }
  }, [userPlayers, wonPlayers, acceptedOffers]); // Depend on userPlayers, wonPlayers, and acceptedOffers to re-initialize when they load

useEffect(() => {
    if (userPendingTransactionsData.Items && premPlayersData.length > 0) {
      const wonBids = userPendingTransactionsData.Items.filter(bid => bid.status === "win" && bid.window == getCurrentWindow());
      const players = wonBids.map(bid => {
        const fullPlayerData = premPlayersData.find(p => p.playerId === bid.playerId);
        return { ...bid, ...fullPlayerData };
      });
      setWonPlayers(players);
    }
  }, [userPendingTransactionsData, premPlayersData]);

  const handlePlayerSelect = (playerId, isSelected) => {
    setSelectedPlayers(prevSelectedPlayers => ({
      ...prevSelectedPlayers,
      [playerId]: isSelected,
    }));
  };

  const numberOfSelectedPlayers = Object.values(selectedPlayers).filter(Boolean).length;

  const confirmTeam = async () => {
    const selectedPlayerIds = Object.keys(selectedPlayers)
      .filter((playerId) => selectedPlayers[playerId])
      .map(Number); // Cast to Number

    try {
      const clubId = user['https://tlog-online.co.uk/club_id'];
      const windowId = siteVarsData.game_vars.currentWindow
      const { data, error } = await confirmTeamResource(clubId, windowId, selectedPlayerIds);

      if (data) {
        console.log("Team confirmed successfully:", data);
        setIsTeamConfirmed(true); // Set to true on successful confirmation
        // Optionally, provide user feedback or refresh data
      }

      if (error) {
        console.error("Error confirming team:", error);
        // Optionally, display an error message to the user
      }
    } catch (error) {
      console.error("Error getting access token or confirming team:", error);
    }
  };

  const allPlayers = [...userPlayers, ...wonPlayers];

  const clubId = user ? user['https://tlog-online.co.uk/club_id'] : null;

  const playersAfterOffers = allPlayers.filter(player => {
    // Remove players that have been transferred FROM the user's club
    const transferredFromUserClub = acceptedOffers.some(offer =>
      offer.from == clubId && offer.playerId == player.playerId
    );
    return !transferredFromUserClub;
  });

  const playersAddedByOffers = acceptedOffers.filter(offer =>
    offer.to == clubId && !playersAfterOffers.some(player => player.playerId == offer.playerId)
  ).map(offer => ({
    ...offer,
    isOfferPlayer: true, // Mark as an offer player
  }));

  const finalPlayers = [...playersAfterOffers, ...playersAddedByOffers].sort((a, b) => {
    if (a.positionId !== b.positionId) {
      return a.positionId - b.positionId;
    }
    return a.lastName.localeCompare(b.lastName);
  });

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1, marginLeft: 1 }}>
        <Typography variant="h10" component="div" sx={{ marginRight: 2 }}>
          Selected Players: {numberOfSelectedPlayers}/11
        </Typography>
        <Button
          variant="contained"
          color="primary"
          disabled={numberOfSelectedPlayers !== 11 || isTeamConfirmed}
          onClick={confirmTeam}
        >
          Confirm Team
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ overflowY: 'auto', marginBottom:5 }}>
      {(isLoadingPremPlayersData || isLoadingUserPendingTransactionsData || isLoadingTeamData || isLoadingAcceptedOffers) && (
        <Stack spacing={1}>
          <Skeleton variant="rounded" width={"100%"} height={30} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
        </Stack>
      )}
      {(!isLoadingPremPlayersData && !isLoadingUserPendingTransactionsData && !isLoadingTeamData && !isLoadingAcceptedOffers) && (
        <Table sx={{ minWidth: 390 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ paddingLeft: 6, paddingRight:0,paddingBottom: 0 }}></TableCell>
              <TableCell style={{ paddingLeft: 6, paddingRight:6}}>Player</TableCell>
              <TableCell style={{ paddingLeft: 6, paddingRight:6}}>Status</TableCell>
              <TableCell style={{ paddingLeft: 6, paddingRight:6}}>Keep</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {finalPlayers.map((player) => {
              if (!player) return null; // Skip if player data is not found

              const isWonPlayer = player.bidId ? true : false;
              const isOfferPlayer = player.isOfferPlayer ? true : false;

              return (
                <TableRow key={player.playerId}>
                  <TableCell style={{ paddingLeft: 6, paddingRight:0, paddingBottom: 0 }}>
                    <img src={`https://resources.premierleague.com/premierleague25/photos/players/110x140/${player.photo.substr(0, player.photo.indexOf('.'))}.png`} style={{ width: '40px', height: '51px' }} />
                  </TableCell>
                  <TableCell style={{ paddingLeft: 6, paddingRight:6}}>
                    <span>{player.lastName}</span>
                    <br />
                    <span style={{fontWeight: 'bold'}}>{getPlayerPositionById(player.positionId).name_short}</span> {getTeamById(player.teamId).name_short}
                  </TableCell>
                  <TableCell style={{ paddingLeft: 6, paddingRight:6}}>
                    {isWonPlayer ? "Won Bid" : isOfferPlayer ? "Accepted Offer" : "Current Team"}
                  </TableCell>
                  <TableCell style={{ paddingLeft: 6, paddingRight:6}}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!selectedPlayers[player.playerId]}
                          onChange={(event) => handlePlayerSelect(player.playerId, event.target.checked)}
                          disabled={isWonPlayer || isOfferPlayer || isTeamConfirmed}
                        />
                      }
                      label=""
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TableContainer>
    </React.Fragment>
  );
};